import { useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import Breadcrumb from '../components/Breadcrumb'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Link } from 'react-router'

gsap.registerPlugin(ScrollTrigger)

const sections = [
  {
    id: 'introduction',
    title: 'INTRODUCTION',
    content: `Welcome to Technifire Software ("Technifire", "we", "us", or "our"). This Privacy Policy explains how we collect, use, store, and protect your information when you access or use our services—including our website at https://technifire.io, our applications, and related services (collectively, the "Service").

By using the Service, you agree to the terms outlined in this policy. If you do not agree, you should not use the Service.`
  },
  {
    id: 'who-we-are',
    title: 'WHO WE ARE',
    content: `Technifire Software is a digital agency based in Miami, Florida, USA. We provide web development, SaaS platform development, e-commerce solutions, CRM systems, branding & design, landing pages, and AI automation & chatbot services.`
  },
  {
    id: 'information-we-collect',
    title: 'INFORMATION WE COLLECT',
    content: `Personal Information
We may collect personally identifiable information that you voluntarily provide when using our Service. This can include your name, email address, phone number, company name, billing information, and other contact details. This information is typically collected when you fill out a contact form, request a quote, subscribe to our newsletter, or communicate with us directly.

Usage Information
When you interact with our website, we may automatically collect technical data such as your IP address, browser type, pages visited, time and duration of your visit, and referring URL. If you access our Service from a mobile device, we may also collect device information such as model, operating system, mobile browser type, and device identifiers.

Cookies and Tracking Technologies
We use cookies and similar tracking technologies to operate our website and understand user behavior. Cookies are small text files stored on your device to help remember preferences or login state. You may set your browser to refuse cookies, but some features of the Service may not function as expected without them.`
  },
  {
    id: 'how-we-use-information',
    title: 'HOW WE USE INFORMATION',
    content: `The information we collect is used to operate and improve the Service, respond to your inquiries, provide project consultations, send service updates, and fulfill contractual obligations. Communications from Technifire will typically relate to project discussions, proposals, support, or service announcements. You may opt out of non-essential communications at any time by contacting us or using the unsubscribe link in emails.`
  },
  {
    id: 'data-retention',
    title: 'DATA RETENTION',
    content: `We retain personal data for as long as your account or inquiry is active, or as needed to provide the Service and fulfill legal obligations. You may request deletion of your data by contacting us at privacy@technifire.software. In some cases, we may retain limited information for legal, security, or fraud prevention purposes.`
  },
  {
    id: 'disclosure-of-data',
    title: 'DISCLOSURE OF DATA',
    content: `Technifire does not sell, lease, or rent your personal data to third parties. We may disclose data only in specific situations: when required by law, to trusted service providers under contract (e.g., hosting, analytics, email delivery), or during a business transaction such as a merger or acquisition. All disclosures are made in accordance with applicable laws and subject to appropriate safeguards.`
  },
  {
    id: 'international-transfers',
    title: 'INTERNATIONAL TRANSFERS',
    content: `If you access the Service from outside the United States, please be aware that your information may be transferred to and processed in the U.S. By using the Service, you consent to this transfer in compliance with applicable legal standards.`
  },
  {
    id: 'security',
    title: 'SECURITY',
    content: `We implement reasonable technical and organizational measures to protect your personal data, including encryption, secure access controls, and regular security reviews. While we strive to protect your information, no system can guarantee 100% security.`
  },
  {
    id: 'your-rights',
    title: 'YOUR RIGHTS',
    content: `Depending on your jurisdiction, you may have the right to access, update, or delete your personal data, as well as object to certain processing activities. To exercise these rights, please contact privacy@technifire.software. We may need to verify your identity before fulfilling your request.`
  },
  {
    id: 'childrens-privacy',
    title: 'CHILDREN\'S PRIVACY',
    content: `Our Services are not intended for individuals under 13 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with data, please contact us immediately and we will take appropriate action.`
  },
  {
    id: 'changes-to-policy',
    title: 'CHANGES TO THIS POLICY',
    content: `We may update this Privacy Policy occasionally to reflect changes in our practices, legal obligations, or Service features. When we make changes, we will revise the "Last Updated" date at the top of this page. Continued use of the Service after changes means you accept the updated terms.`
  },
  {
    id: 'contact-us',
    title: 'CONTACT US',
    content: `If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:

Technifire LLC
8301 State Line Rd Ste 220 # 3747, Kansas City, MO 64114-2025
Email: info@technifire.io
Phone: +1 (425) 541-4001
Website: https://technifire.io

Last Updated: July 16, 2026`
  }
]

export default function PrivacyPolicy() {
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.policy-animate')
      if (elements && elements.length > 0) {
        gsap.fromTo(elements, { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top bottom', toggleActions: 'play none none none', invalidateOnRefresh: true }
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  return (
    <div className="min-h-screen bg-[#050505] pb-20" ref={sectionRef}>
      <Helmet>
        <title>Privacy Policy — Technifire Software</title>
        <meta name="description" content="Technifire Software Privacy Policy — how we collect, use, store, and protect your personal information when using our services." />
        <link rel="canonical" href="https://technifire.io/privacy-policy" />
        <meta property="og:title" content="Privacy Policy — Technifire Software" />
        <meta property="og:description" content="Technifire Software Privacy Policy — how we collect, use, store, and protect your information." />
        <meta property="og:url" content="https://technifire.io/privacy-policy" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Breadcrumb current="PRIVACY POLICY" />

      {/* Hero */}
      <div className="px-6 lg:px-10 pt-8 pb-12">
        <div className="max-w-4xl policy-animate">
          <span className="text-mono text-[#9A9A9A] mb-3 block">PRIVACY POLICY</span>
          <h1 className="text-h2 uppercase text-white mb-6">
            YOUR PRIVACY<br />MATTERS TO US
          </h1>
          <p className="text-lg text-[#9A9A9A] leading-relaxed">
            This policy explains how Technifire Software collects, uses, stores, and protects your personal information when you use our services.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-sm text-[#9A9A9A]">
            <span>Last Updated: July 16, 2026</span>
            <span className="text-white/30 mx-2">|</span>
            <span>Version 1.0</span>
            <span className="text-white/30 mx-2">|</span>
            <a href="mailto:privacy@technifire.software" className="text-white hover:text-[#9A9A9A] transition-colors">Contact Privacy Team</a>
          </div>
        </div>
      </div>

      {/* Policy Sections */}
      <div className="px-6 lg:px-10">
        <div className="max-w-4xl space-y-12">
          {sections.map((section, index) => (
            <section key={section.id} id={section.id} className="policy-animate border-y border-white/10 py-10 first:pt-0 last:pb-0">
              <span className="text-mono text-[#9A9A9A] mb-4 block">
                {String(index + 1).padStart(2, '0')} / {sections.length}
              </span>
              <h2 className="text-h3 text-white mb-6">{section.title}</h2>
              <div className="prose prose-invert max-w-none text-[#9A9A9A] leading-relaxed">
                {section.content.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="mb-4">{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 lg:px-10 py-16 policy-animate">
        <div className="bg-gradient-to-r from-[#111] to-[#0a0a0a] rounded-2xl p-8 lg:p-12 border border-white/5 text-center max-w-2xl mx-auto">
          <h2 className="text-h3 text-white mb-4">Have Questions About Your Data?</h2>
          <p className="text-[#9A9A9A] mb-8">Our privacy team is here to help. Contact us anytime for questions about your data or this policy.</p>
          <Link to="/contacts" className="btn-primary inline-flex justify-center">
            CONTACT PRIVACY TEAM
          </Link>
        </div>
      </div>
    </div>
  )
}